import { Building, Mail, Phone, Plus, Trash2, User } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

interface Contact {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  department: string;
  isPrimary: boolean;
}

export function ContactInformation() {
  const [contacts, setContacts] = useState<Contact[]>([
    {
      id: '1',
      name: '',
      title: '',
      email: '',
      phone: '',
      department: '',
      isPrimary: true,
    },
  ]);

  const departments = [
    'Executive',
    'Finance',
    'Operations',
    'IT',
    'HR',
    'Sales',
    'Marketing',
    'Legal',
    'Other',
  ];

  const addContact = () => {
    const newContact: Contact = {
      id: Date.now().toString(),
      name: '',
      title: '',
      email: '',
      phone: '',
      department: '',
      isPrimary: false,
    };
    setContacts([...contacts, newContact]);
  };

  const removeContact = (id: string) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((contact) => contact.id !== id));
    }
  };

  const updateContact = (id: string, field: keyof Contact, value: string | boolean) => {
    setContacts(
      contacts.map((contact) => (contact.id === id ? { ...contact, [field]: value } : contact))
    );
  };

  const setPrimaryContact = (id: string) => {
    setContacts(
      contacts.map((contact) => ({
        ...contact,
        isPrimary: contact.id === id,
      }))
    );
  };

  return (
    <div className="space-y-6">
      {contacts.map((contact, index) => (
        <Card key={contact.id}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <CardTitle className="text-lg">
                {contact.isPrimary ? 'Primary Contact' : `Contact ${index + 1}`}
              </CardTitle>
              {contact.isPrimary && (
                <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                  Primary
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!contact.isPrimary && (
                <Button variant="outline" size="sm" onClick={() => setPrimaryContact(contact.id)}>
                  Set as Primary
                </Button>
              )}
              {contacts.length > 1 && (
                <Button variant="outline" size="sm" onClick={() => removeContact(contact.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`name-${contact.id}`}>
                  Full Name <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id={`name-${contact.id}`}
                    className="pl-9"
                    placeholder="Enter full name"
                    value={contact.name}
                    onChange={(e) => updateContact(contact.id, 'name', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`title-${contact.id}`}>
                  Job Title <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id={`title-${contact.id}`}
                    className="pl-9"
                    placeholder="e.g., CFO, VP Finance"
                    value={contact.title}
                    onChange={(e) => updateContact(contact.id, 'title', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`email-${contact.id}`}>
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id={`email-${contact.id}`}
                    type="email"
                    className="pl-9"
                    placeholder="contact@company.com"
                    value={contact.email}
                    onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`phone-${contact.id}`}>Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id={`phone-${contact.id}`}
                    type="tel"
                    className="pl-9"
                    placeholder="+1 (555) 123-4567"
                    value={contact.phone}
                    onChange={(e) => updateContact(contact.id, 'phone', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`department-${contact.id}`}>Department</Label>
              <Select onValueChange={(value) => updateContact(contact.id, 'department', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept.toLowerCase()}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      ))}

      <Card className="border-dashed border-2">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Plus className="w-8 h-8 text-muted-foreground mb-2" />
          <p className="text-muted-foreground mb-4">Add additional contact</p>
          <Button variant="outline" onClick={addContact}>
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
